import { useState, useEffect, useCallback } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { UserX, RotateCcw, AlertTriangle, Mail, Phone, Calendar, Play } from "lucide-react";

/**
 * Admin → Account Deletions tab (GDPR).
 *
 * Lists all users currently in the 30-day deletion grace period (most
 * recent first). For each, admin can:
 *   - Cancel the deletion (restores listings if host).
 *   - View the reason given by the user.
 *   - Manually trigger the deletion cron (for QA / urgent removal).
 *
 * Admin can also schedule another user for deletion via the input at
 * the top (paste user_id). Reasoning: ToS violations, fraud, etc.
 */
export default function DeletionsTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [forceId, setForceId] = useState("");
  const [forceReason, setForceReason] = useState("");

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/users/pending-deletion");
      setItems(r.data.items || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load pending deletions");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const cancel = async (userId) => {
    if (!window.confirm("Cancel scheduled deletion for this user? Their listings (if any) will be reactivated.")) return;
    try {
      await api.post(`/admin/users/${userId}/cancel-deletion`);
      toast.success("Deletion cancelled");
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to cancel"); }
  };

  const forceSchedule = async () => {
    if (!forceId.trim()) { toast.error("Paste a user id first"); return; }
    if (!window.confirm(`Schedule user ${forceId.slice(0,8)}... for deletion in 30 days?\nReason: ${forceReason || "(none)"}`)) return;
    try {
      await api.post(`/admin/users/${forceId.trim()}/request-deletion`, { reason: forceReason });
      toast.success("Scheduled. User will be notified via email.");
      setForceId(""); setForceReason(""); fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to schedule"); }
  };

  const runCron = async () => {
    if (!window.confirm("Run the deletion cron now? Anyone whose 30-day window has expired will be hard-deleted immediately.")) return;
    try {
      const r = await api.post("/admin/cron/account-deletions/run");
      toast.success(`Cron ran: ${r.data.hard_deleted} hard-deleted · ${r.data.reminded} reminded`);
      fetchAll();
    } catch (e) { toast.error(e.response?.data?.detail || "Cron failed"); }
  };

  return (
    <div className="space-y-4" data-testid="deletions-tab">
      <div className="bg-[#FEE2E2] border border-[#FCA5A5] rounded-xl p-3 flex items-center justify-between gap-3">
        <div className="text-xs text-[#7F1D1D] flex-1">
          <p className="font-semibold flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> GDPR Account Deletion Queue</p>
          <p className="mt-0.5 text-[#991B1B]">Users in the 30-day grace period. Hard-delete fires automatically when the window expires. Manual cancel restores listings if applicable.</p>
        </div>
        <Button size="sm" variant="outline" className="rounded-full text-xs flex-shrink-0" onClick={runCron} data-testid="run-deletion-cron">
          <Play className="w-3.5 h-3.5 mr-1" /> Run cron now
        </Button>
      </div>

      {/* Admin-force schedule */}
      <div className="bg-white border border-[#E5E3DB] rounded-xl p-4" data-testid="admin-force-schedule">
        <h4 className="font-['Outfit'] font-semibold text-sm text-[#1A231C] mb-2">Schedule a user for deletion (admin override)</h4>
        <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2">
          <input
            value={forceId}
            onChange={(e) => setForceId(e.target.value)}
            placeholder="user id (uuid)"
            className="rounded-md border border-[#E5E3DB] px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-[#2C5F2D]"
            data-testid="force-delete-user-id"
          />
          <Textarea
            value={forceReason}
            onChange={(e) => setForceReason(e.target.value)}
            placeholder="Reason (ToS violation, fraud, customer request via email, etc.) — required for audit"
            rows={1}
            className="text-sm"
            data-testid="force-delete-reason"
          />
          <Button onClick={forceSchedule} className="rounded-full bg-red-600 hover:bg-red-700 text-white" data-testid="force-delete-btn">
            <UserX className="w-4 h-4 mr-1" /> Schedule
          </Button>
        </div>
      </div>

      {/* Queue */}
      {loading ? (
        <div className="text-center py-12 text-[#8A998E] text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="text-center py-12 text-[#8A998E] text-sm" data-testid="deletions-empty">No pending deletions. Inbox zero ✨</div>
      ) : (
        <div className="space-y-2">
          {items.map(u => {
            const sched = u.scheduled_hard_delete_at ? new Date(u.scheduled_hard_delete_at) : null;
            const days = sched ? Math.max(0, Math.ceil((sched - new Date()) / 86400000)) : "—";
            return (
              <div key={u.id} className="bg-white border border-[#E5E3DB] rounded-xl p-4 flex items-center gap-4 flex-wrap" data-testid={`deletion-item-${u.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-['Outfit'] font-semibold text-[#1A231C] text-sm">{u.name || "Unnamed user"}</span>
                    <span className="text-xs bg-[#F3F2EE] text-[#5C6B60] rounded-full px-2 py-0.5 capitalize">{u.role}</span>
                    <span className="text-xs bg-red-50 text-red-700 border border-red-200 rounded-full px-2 py-0.5 font-medium">{days} day{days === 1 ? "" : "s"} left</span>
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs text-[#5C6B60] flex-wrap">
                    <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {u.email}</span>
                    {u.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" /> {u.phone}</span>}
                    {sched && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> Hard-delete on {sched.toLocaleDateString()}</span>}
                  </div>
                  {u.deletion_reason && <div className="mt-1 text-xs text-[#1A231C] italic">"{u.deletion_reason}"</div>}
                  <div className="mt-0.5 text-[10px] text-[#8A998E]">Requested by: {u.deletion_requested_by || "self"}</div>
                </div>
                <Button size="sm" variant="outline" className="rounded-full text-xs" onClick={() => cancel(u.id)} data-testid={`cancel-deletion-${u.id}`}>
                  <RotateCcw className="w-3 h-3 mr-1" /> Cancel deletion
                </Button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
